import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** Value of the "Deleted" option. It is not a real status: it switches the list to its deleted items (GH #331). */
export const DELETED_STATUS = '__deleted__';

export type StatusOption = { value: string; label: string };

/**
 * "Deleted" is shown in red wherever it is offered or picked, so it never reads as a regular status.
 * The option keeps the colour while highlighted; the trigger (or combobox button) turns red when it is picked.
 */
export const deletedOptionClassName = 'text-destructive focus:text-destructive data-[selected=true]:text-destructive';
export const deletedTriggerClassName = 'border-destructive text-destructive';

/** Icon and label of the "Deleted" status option. */
export function DeletedStatusLabel() {
	const { t } = useTranslation();
	return (
		<>
			<Trash2 className="size-3.5 text-destructive" aria-hidden="true" />
			{t('common.deleted.status')}
		</>
	);
}

type StatusFilterSelectProps = {
	value: string;
	onValueChange: (value: string) => void;
	options: StatusOption[];
	/** The user may see deleted items (the entity's delete role); adds "Deleted" at the end */
	canShowDeleted: boolean;
	ariaLabel: string;
	className?: string;
};

/**
 * Status filter of a list, with "Deleted" as the last option below a separator: to the user, deleted items
 * are one more status, never shown together with the others (GH #331).
 */
export function StatusFilterSelect({
	value,
	onValueChange,
	options,
	canShowDeleted,
	ariaLabel,
	className,
}: StatusFilterSelectProps) {
	return (
		<Select value={value} onValueChange={onValueChange}>
			<SelectTrigger
				aria-label={ariaLabel}
				className={cn(
					'bg-white text-sm h-8 w-40 shrink-0',
					value === DELETED_STATUS && deletedTriggerClassName,
					className,
				)}
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent position="popper">
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						{option.label}
					</SelectItem>
				))}
				{canShowDeleted && (
					<>
						<SelectSeparator />
						<SelectItem value={DELETED_STATUS} className={deletedOptionClassName}>
							<DeletedStatusLabel />
						</SelectItem>
					</>
				)}
			</SelectContent>
		</Select>
	);
}
